import React, { useState, useMemo } from 'react';
import ZoneManagerMap from '../../../components/ZoneManagerMap';
import { styles } from './adminStyles';
import { resolveActiveLayerName } from './layerUtils';

const FOCUS_AREA_INSTRUCTIONS = "This layer limits where valid bids can be made. It also allows you to give different weightings to particular locations. This would improve bid scores over and above their actual outcome performance eg to give preference to bids in upper parts of a catchment, scores in this area could be increased by 10%. Layers are uploaded and managed from the Layer Library tab.";

// The Auction Focus Area section — uploading and archiving/deleting layers
// happens in the Layer Library tab; this section only applies an existing
// library layer to one auction and owns its own map instance. Opened
// directly from the Summary of Auctions table's Layers column (formerly a
// "Zones" sub-tab inside the Auction Management modal).
const LayerSection = ({
  title, instructions, layerType, zones, layers, globalZones, isClosed,
  onApplyExisting, onClearAll, onEditZone, onDeleteZone
}) => {
  const [selectedLayerId, setSelectedLayerId] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [status, setStatus] = useState('');

  // Stable reference across re-renders (e.g. keystrokes elsewhere on the
  // page) so ZoneManagerMap's fit-to-extent effect doesn't get retriggered
  // (and its in-flight view.goTo() cancelled) by an unrelated parent render.
  const sectionZones = useMemo(() => zones.filter(z => z.type === layerType), [zones, layerType]);
  const activeLayerLabel = resolveActiveLayerName(sectionZones, layers);

  const handleSelectLayer = async (e) => {
    const layerId = e.target.value;
    setSelectedLayerId(layerId);
    if (!layerId) return;
    setIsBusy(true); setStatus('');
    try {
      await onApplyExisting(layerId);
      setStatus('✅ Layer Applied!');
      setTimeout(() => setStatus(''), 3000);
    } catch (err) {
      setStatus('❌ Apply Failed: ' + (err.response?.data?.msg || 'Error'));
    } finally { setIsBusy(false); }
  };

  const handleClear = async () => {
    if (!window.confirm(`Delete ALL ${title} zones for this event?`)) return;
    setIsBusy(true); setStatus('');
    try {
      await onClearAll();
      setStatus('✅ Cleared!');
      setTimeout(() => setStatus(''), 3000);
    } catch (err) {
      setStatus('❌ Failed to clear.');
    } finally { setIsBusy(false); }
  };

  return (
    <div style={styles.card}>
      <h3 style={{marginTop:0, ...styles.h3}}>{title}</h3>
      <div style={styles.instructionBox}>{instructions}</div>

      {isClosed ? (
        <div style={{padding: '15px', backgroundColor: '#F8FAFC', color: '#475569', border: '1px solid #ddd', borderRadius: '4px', fontFamily: '"Inter", sans-serif', fontSize: '0.9rem', textAlign: 'center'}}>
            🔒 Map layer editing is disabled because this event is closed.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '30px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>

            <div style={{padding:'15px', background:'#F8FAFC', borderRadius:'5px', border:'1px solid #ddd'}}>
              <h4 style={{marginTop:0, fontSize:'0.95rem', color: '#0F172A', fontFamily: '"Plus Jakarta Sans", sans-serif'}}>Select a Focus Area</h4>
              <select
                style={{...styles.select, marginBottom: 0}}
                value={selectedLayerId}
                onChange={handleSelectLayer}
                disabled={isBusy}
              >
                <option value="">-- Choose a saved layer --</option>
                {layers.map(l => (
                  <option key={l._id} value={l._id}>{l.name}</option>
                ))}
              </select>
              {status && <div style={{ marginTop: '10px', fontSize:'0.85rem', fontWeight: 'bold', color: status.includes('Failed') ? '#F59E0B' : '#0D9488' }}>{status}</div>}
            </div>

            <div style={{padding:'15px', background:'#F8FAFC', borderRadius:'5px', border: '1px solid #ddd', display: 'flex', flexDirection: 'column', gap: '10px'}}>
              <p style={{margin: 0, fontSize:'0.8rem', color:'#475569', fontFamily: '"Inter", sans-serif'}}>
                To upload a new layer or archive/delete an existing one, visit the <strong>Layer Library</strong> tab.
              </p>
              <button type="button" disabled={isBusy} onClick={handleClear} style={styles.btnDangerOutline}>Clear Focus Area Layer</button>
            </div>
          </div>

          <div style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', border: '1px solid #eee', padding: 0, overflow: 'hidden', minHeight: '500px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '10px 15px', borderBottom: '1px solid #eee', backgroundColor: '#F8FAFC', fontSize: '0.85rem', color: '#475569', fontFamily: '"Inter", sans-serif' }}>
              <strong style={{color: '#0F172A'}}>Active Layer:</strong> {activeLayerLabel}
            </div>
            <div style={{flex: 1, position: 'relative'}}>
              <ZoneManagerMap zones={sectionZones} layerType={layerType} fallbackZones={globalZones} onEditZone={onEditZone} onDeleteZone={onDeleteZone} loading={isBusy} isClosed={isClosed} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Modal chrome wrapping LayerSection — opened directly from the Summary of
// Auctions table's Layers column (AdminOverview.js), rather than a sub-tab
// buried inside the Edit Auction modal.
const ZonesModal = ({
  auctionName, onClose,
  zones, focusAreaLayers, globalZones, isClosed,
  onApplyExisting, onClearAll, onEditZone, onDeleteZone
}) => {
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={{...styles.modal, display: 'flex', flexDirection: 'column', overflow: 'hidden'}} onClick={(e) => e.stopPropagation()}>
        <div style={{padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC', flexShrink: 0}}>
          <h2 style={{margin: 0, ...styles.h2}}>Event Focus Area {auctionName ? `— ${auctionName}` : ''}</h2>
          <button onClick={onClose} style={{ ...styles.btnReversed, padding: '8px 16px' }}>Close ✕</button>
        </div>
        <div style={{flex: 1, overflowY: 'auto', padding: '20px', backgroundColor: '#F8FAFC'}}>
          <LayerSection
            title="Event Focus Area"
            instructions={FOCUS_AREA_INSTRUCTIONS}
            layerType="priority"
            zones={zones}
            layers={focusAreaLayers}
            globalZones={globalZones}
            isClosed={isClosed}
            onApplyExisting={onApplyExisting}
            onClearAll={onClearAll}
            onEditZone={onEditZone}
            onDeleteZone={onDeleteZone}
          />
        </div>
      </div>
    </div>
  );
};

export default ZonesModal;
